import { Category, Operation, Settings } from '../api/types';
import { computeDailyBudget } from './daily-budget';
import { daysInCurrentMonth } from './horizons';
import { todayIso } from './format';

/**
 * «Бюджет месяца» — модель из трёх пулов (валидирована ресерчем: Monarch flex
 * budgeting, Monzo left-to-spend, Daily Budget Original):
 *
 *   доход (ожидаемый) − фикс-обязательства − 🛡 цель месяца = флекс-пул на повседневное
 *
 * Динамический дневной лимит = остаток флекс-пула / оставшиеся дни.
 * Это pace-индикатор (Monzo-стиль): вчера пережал — сегодня мягче, не «живи на 0».
 */

export interface MonthBudget {
  /** Динамический режим активен: цель задана и нет ручного override. */
  active: boolean;
  monthTarget: number;

  incomeFact: number;
  incomePlan: number;
  /** max(факт, план) — до прихода зарплаты считаем по плану. */
  expectedIncome: number;

  /** Нерегулярные (is_daily=0): факт + ожидаемый остаток по планам. */
  fixedSpent: number;
  fixedExpected: number;
  fixedRemaining: number;
  /** Нерегулярные категории с неизрасходованным планом — карта резервов для перераспределения. */
  fixedBreakdown: { name: string; icon: string; remaining: number }[];

  /** Повседневный (флекс) пул. */
  flexPlan: number;
  flexSpent: number;
  flexSpentToday: number;
  /** Всего на повседневку в этом месяце после дохода, фикса и цели. */
  availableFlexTotal: number;
  /** Остаток флекса на сегодня+будущее (без сегодняшних трат). */
  flexLeftStartOfDay: number;

  daysLeft: number;
  dynamicDailyLimit: number;
  staticDailyLimit: number;

  /** Остаток повседневного пула на остаток месяца (вкл. сегодня). */
  flexLeftNow: number;
  /** Дней до конца недели вкл. сегодня (не больше остатка месяца). */
  daysLeftThisWeek: number;
  /** Сколько можно потратить за остаток недели, держась месячного темпа. */
  weekSafeToSpend: number;
  /** Ожидаемый расход повседневного к этому дню при ровном темпе. */
  expectedFlexByNow: number;
  /** Запас/перебор темпа: expectedFlexByNow − flexSpent (+ = идёшь экономно). */
  paceDelta: number;
  /** Разовые повседневные траты, выведенные из дневного темпа. */
  oneOffSpent: number;
  /** Неизрасходованный резерв в планах нерегулярных категорий — подушка. */
  fixedReserve: number;
}

export function computeMonthBudget(
  operations: Operation[],
  categories: Category[],
  settings: Settings | null,
  now: Date = new Date(),
  oneOffIds: Set<string> = new Set(),
  releasedReserveCats: Set<string> = new Set(),
): MonthBudget {
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  // «сегодня» — от переданного now, не от системных часов: иначе рассинхрон
  // у полуночи и невоспроизводимость в тестах
  const today = todayIso(now);
  const catByName = new Map(categories.map((c) => [c.name, c] as const));

  let incomeFact = 0;
  let flexSpent = 0;
  let flexSpentToday = 0;
  let oneOffSpent = 0;
  const fixedSpentBy = new Map<string, number>();
  const incomeFactBy = new Map<string, number>();

  for (const op of operations) {
    if (!op.date.startsWith(monthKey)) continue;
    const cat = catByName.get(op.category);
    const t = cat?.type ?? op.type;
    if (t === 'Доход') {
      incomeFact += op.amount;
      incomeFactBy.set(op.category, (incomeFactBy.get(op.category) || 0) + op.amount);
    } else if (t === 'Расход') {
      if (cat && !cat.is_daily) {
        fixedSpentBy.set(cat.name, (fixedSpentBy.get(cat.name) || 0) + op.amount);
      } else if (oneOffIds.has(op.id)) {
        // разовая повседневная трата — не давит дневной темп (фондируется из запаса)
        oneOffSpent += op.amount;
      } else {
        flexSpent += op.amount;
        if (op.date === today) flexSpentToday += op.amount;
      }
    }
  }

  const incomePlan = categories
    .filter((c) => c.type === 'Доход')
    .reduce((s, c) => s + (c.plan_month || 0), 0);

  // По-категорийный max(план, факт): зарплата уже пришла — берём факт,
  // репетиторство ещё идёт — берём план (гарантированный минимум).
  // Всё сверх плана поднимает лимит автоматически в день прихода.
  let expectedIncome = 0;
  for (const c of categories) {
    if (c.type !== 'Доход') continue;
    expectedIncome += Math.max(c.plan_month || 0, incomeFactBy.get(c.name) || 0);
  }
  // доход по категориям, которых уже нет в справочнике — учитываем фактом
  for (const [name, v] of incomeFactBy) {
    if (!catByName.has(name)) expectedIncome += v;
  }

  let fixedSpent = 0;
  let fixedExpected = 0;
  const fixedBreakdown: MonthBudget['fixedBreakdown'] = [];
  for (const c of categories) {
    if (c.type !== 'Расход' || c.is_daily) continue;
    const spent = fixedSpentBy.get(c.name) || 0;
    fixedSpent += spent;
    // освобождённый резерв не держим — категория считается по факту, излишек уходит в повседневное
    const released = releasedReserveCats.has(c.name);
    const expected = released ? spent : Math.max(c.plan_month || 0, spent);
    fixedExpected += expected;
    const remaining = expected - spent;
    if (remaining > 0) fixedBreakdown.push({ name: c.name, icon: c.icon, remaining });
  }
  fixedBreakdown.sort((a, b) => b.remaining - a.remaining);
  const fixedRemaining = fixedExpected - fixedSpent;

  const flexPlan = categories
    .filter((c) => c.type === 'Расход' && c.is_daily)
    .reduce((s, c) => s + (c.plan_month || 0), 0);

  const monthTarget = settings?.month_target ?? 0;
  const override = settings?.daily_budget_override != null && settings.daily_budget_override > 0;
  const active = monthTarget > 0 && !override;

  const availableFlexTotal = expectedIncome - monthTarget - fixedExpected;
  // лимит на сегодня считается от состояния «на утро»: сегодняшние траты
  // вычитаются уже из остатка дня, а не из самого лимита
  const flexLeftStartOfDay = availableFlexTotal - (flexSpent - flexSpentToday);

  const daysInMonth = daysInCurrentMonth(now);
  const dayOfMonth = now.getDate();
  const daysLeft = Math.max(1, daysInMonth - dayOfMonth + 1);
  const dynamicDailyLimit = Math.max(0, flexLeftStartOfDay) / daysLeft;
  const staticDailyLimit = computeDailyBudget(categories, settings, now);

  // Недельный safe-to-spend: поглощает лумпи-траты (продукты раз в неделю),
  // чтобы один поход за продуктами не «блокировал» день.
  const flexLeftNow = Math.max(0, availableFlexTotal - flexSpent);
  const weekPos = (now.getDay() + 6) % 7; // Пн=0 … Вс=6
  const daysLeftThisWeek = Math.min(daysLeft, 7 - weekPos);
  const weekSafeToSpend = dynamicDailyLimit * daysLeftThisWeek;

  // Пейс-сигнал: сколько повседневного «положено» к этому дню при ровном темпе.
  const expectedFlexByNow = availableFlexTotal > 0 ? availableFlexTotal * (dayOfMonth / daysInMonth) : 0;
  const paceDelta = expectedFlexByNow - flexSpent;
  const fixedReserve = Math.max(0, fixedRemaining);

  return {
    active,
    monthTarget,
    incomeFact,
    incomePlan,
    expectedIncome,
    fixedSpent,
    fixedExpected,
    fixedRemaining,
    fixedBreakdown,
    flexPlan,
    flexSpent,
    flexSpentToday,
    availableFlexTotal,
    flexLeftStartOfDay,
    daysLeft,
    dynamicDailyLimit,
    staticDailyLimit,
    flexLeftNow,
    daysLeftThisWeek,
    weekSafeToSpend,
    expectedFlexByNow,
    paceDelta,
    oneOffSpent,
    fixedReserve,
  };
}
